import { useState, useEffect } from 'react'
import { type MRT_PaginationState } from 'material-react-table'
import {
  getReplyReports,
  ignoreReplyReport,
  deleteReplyAndReport,
  type IReplyReportWithDetails,
} from '../../../api/reviews'
import { ReportRepliesTable } from './ReportRepliesTable'
import { ConfirmationDialog } from '../../../components/dialogs/ConfirmationDialog'

interface ReportRepliesViewProps {
  hideHeader?: boolean
}

export const ReportRepliesView = ({ hideHeader = false }: ReportRepliesViewProps) => {
  const [reports, setReports] = useState<IReplyReportWithDetails[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)

  // Dialog states
  const [ignoreDialogOpen, setIgnoreDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState<IReplyReportWithDetails | null>(null)

  useEffect(() => {
    loadReports()
  }, [pagination.pageIndex, pagination.pageSize])

  const loadReports = async () => {
    try {
      setIsLoading(true)
      const page = pagination.pageIndex + 1
      const result = await getReplyReports(page, pagination.pageSize)
      setReports(result.items)
      setRowCount(result.totalItems)
    } catch (error) {
      console.error('Error loading reply reports:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleView = (report: IReplyReportWithDetails) => {
    if (report.fragranceSlug && report.reviewId) {
      window.open(`/fragrance/${report.fragranceSlug}?reviewId=${report.reviewId}&replyId=${report.replyId}`, '_blank')
    } else {
      alert('Unable to navigate: Fragrance or review not found for this reply.')
    }
  }

  const handleIgnore = (report: IReplyReportWithDetails) => {
    setSelectedReport(report)
    setIgnoreDialogOpen(true)
  }

  const handleDelete = (report: IReplyReportWithDetails) => {
    setSelectedReport(report)
    setDeleteDialogOpen(true)
  }

  const confirmIgnore = async () => {
    if (!selectedReport) return

    try {
      await ignoreReplyReport(selectedReport.id)
      setIgnoreDialogOpen(false)
      setSelectedReport(null)
      loadReports()
    } catch (error) {
      console.error('Error ignoring report:', error)
      alert('Failed to ignore report. Please try again.')
    }
  }

  const confirmDelete = async () => {
    if (!selectedReport) return

    try {
      await deleteReplyAndReport(selectedReport.id, selectedReport.replyId)
      setDeleteDialogOpen(false)
      setSelectedReport(null)
      loadReports()
    } catch (error) {
      console.error('Error deleting reply:', error)
      alert('Failed to delete reply. Please try again.')
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-8 pb-6 pt-8 border-searchBorder">
          <h1 className="text-2xl font-bold">Reply Reports</h1>
        </div>
      )}

      {/* Material React Table */}
      <div className="flex-1 overflow-hidden">
        <ReportRepliesTable
          data={reports}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onView={handleView}
          onIgnore={handleIgnore}
          onDelete={handleDelete}
        />
      </div>

      {/* Ignore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={ignoreDialogOpen}
        onClose={() => setIgnoreDialogOpen(false)}
        onConfirm={confirmIgnore}
        title="Ignore Report"
        content={`Are you sure you want to ignore this report? The report will be dismissed but the reply will remain visible.`}
        confirmLabel="Ignore Report"
        cancelLabel="Cancel"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Reply"
        content={`Are you sure you want to delete this reply? This action cannot be undone. The reply by "${selectedReport?.replyAuthorUsername}" will be permanently removed.`}
        confirmLabel="Delete Reply"
        cancelLabel="Cancel"
      />
    </div>
  )
}
